/**
 * Ed Hardy Swim — "Build your bundle".
 *
 * A real 1 / 2 / 3 quantity bundle with tiered discounts, for products whose
 * family is a set of prints or colourways rather than a top-and-bottom pair:
 * mesh dresses, cover-ups and the PVC bags.
 *
 * Design notes worth keeping:
 *
 *  - Every slot is PRE-FILLED (product and variant). An empty slot meant the
 *    first press of Add to bag only produced a small error line, which reads as
 *    "the bundle is broken". Everything is changeable, but it always works on
 *    the first click.
 *  - The variant chooser is generic. Apparel picks a Size; the PVC bags use
 *    variants for colourway, so the same control shows Colour. Without this a
 *    three-bag bundle would silently be the same colour three times.
 *  - Totals are compare-at aware: the struck price is the real was-price
 *    (sum of compare_at), so the bundle shows the sale saving AND the bundle
 *    saving rather than an unexplained number.
 */

import {
  loadCatalog,
  bestsellerRank,
  sorter,
  shortVariantLabel,
  NO_BUNDLE,
  openCartDrawer,
} from '@eh/catalog';

const root = () => window.Shopify?.routes?.root || '/';

const money = (n) =>
  window.Shopify?.formatMoney
    ? window.Shopify.formatMoney(Math.round(n * 100))
    : `$${n.toFixed(2).replace(/\.00$/, '')}`;

const thumb = (src, w) =>
  src ? src.replace(/(\.(jpg|jpeg|png|webp))(\?|$)/i, `_${w}x$1$3`) : '';

const esc = (s) => String(s).replace(/"/g, '&quot;');

const plural = (word, n) =>
  n === 1 ? word : /(s|x|z|ch|sh)$/i.test(word) ? `${word}es` : `${word}s`;

/** Which categories a product is allowed to mix with, when set to "auto". */
const FAMILY = {
  dress: ['dress', 'coverup'],
  coverup: ['dress', 'coverup'],
  bag: ['bag'],
  onepiece: ['onepiece'],
};

const NOUN = { dress: 'dress', coverup: 'dress', bag: 'bag', onepiece: 'piece' };

/**
 * Live price for a specific variant.
 *
 * Reading the product-level price uses whichever variant happened to be first
 * in stock, so the totals never moved when the shopper changed size or colour.
 * Always price the variant that is actually selected.
 */
function variantMoney(product, variantId) {
  const vs = product.variants || [];
  const v =
    vs.find((x) => String(x.id) === String(variantId)) ||
    vs.find((x) => x.available) ||
    vs[0] ||
    {};
  const price = parseFloat(v.price != null ? v.price : product.price) || 0;
  const cmp = parseFloat(v.compare_at_price || 0) || 0;
  return { price, was: cmp > price ? cmp : price };
}

const ZOOM_ICON = `
  <svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true" focusable="false">
    <circle cx="9" cy="9" r="5.2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M12.9 12.9 17 17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
  </svg>`;

/*
 * A style rail tile is too small to judge a dress by. The magnifier opens the
 * product's own photographs in a swipeable panel — look only, no buying, so it
 * never competes with the bundle you are in the middle of building.
 */
let lightbox = null;

function openImages(product) {
  if (!lightbox) {
    lightbox = document.createElement('dialog');
    lightbox.className = 'ehbb-lb';
    document.body.appendChild(lightbox);
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox || e.target.closest('[data-lb-close]')) lightbox.close();
    });
  }

  const imgs = product.images && product.images.length ? product.images : [product.image];
  lightbox.innerHTML = `
    <div class="ehbb-lb__inner">
      <button type="button" class="ehbb-lb__x" data-lb-close aria-label="Close">
        <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="1.6" fill="none"/></svg>
      </button>
      ${
        imgs.length > 1
          ? `<button type="button" class="ehbb-lb__nav ehbb-lb__nav--prev" data-step="-1" aria-label="Previous image">
               <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M12.5 4 6.5 10l6 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
             </button>
             <button type="button" class="ehbb-lb__nav ehbb-lb__nav--next" data-step="1" aria-label="Next image">
               <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M7.5 4l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
             </button>`
          : ''
      }
      <div class="ehbb-lb__track" data-lb-track>
        ${imgs
          .map(
            (src, i) =>
              `<div class="ehbb-lb__slide"><img src="${thumb(src, 760)}" alt="${esc(
                product.title
              )} image ${i + 1}" loading="${i ? 'lazy' : 'eager'}"></div>`
          )
          .join('')}
      </div>
      ${
        imgs.length > 1
          ? `<div class="ehbb-lb__dots">${imgs
              .map(
                (_, i) =>
                  `<button type="button" class="ehbb-lb__dot${
                    i ? '' : ' is-on'
                  }" data-go="${i}" aria-label="Image ${i + 1}"></button>`
              )
              .join('')}</div>`
          : ''
      }
      <p class="ehbb-lb__name">${product.title}</p>
    </div>`;

  const track = lightbox.querySelector('[data-lb-track]');
  const dots = lightbox.querySelectorAll('[data-go]');
  /*
   * Smooth scrollTo is ignored inside the dialog (same scroll-snap conflict as
   * the product gallery), so set the offset directly.
   */
  const goTo = (n) => {
    track.scrollLeft = track.clientWidth * Math.min(imgs.length - 1, Math.max(0, n));
  };

  dots.forEach((d) => d.addEventListener('click', () => goTo(Number(d.dataset.go))));
  if (dots.length) {
    track.addEventListener('scroll', () => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      dots.forEach((d, n) => d.classList.toggle('is-on', n === i));
    });
  }

  lightbox.querySelectorAll('[data-step]').forEach((b) =>
    b.addEventListener('click', () =>
      goTo(Math.round(track.scrollLeft / track.clientWidth) + Number(b.dataset.step))
    )
  );

  if (typeof lightbox.showModal === 'function') lightbox.showModal();
  else lightbox.setAttribute('open', '');
}

class EhBundleBuilder extends HTMLElement {
  async connectedCallback() {
    this.handle = this.dataset.handle;

    this.tiers = [1, 2, 3]
      .map((n) => ({
        qty: parseInt(this.dataset[`t${n}Qty`] || n, 10),
        percent: parseFloat(this.dataset[`t${n}Percent`] || '0'),
        code: (this.dataset[`t${n}Code`] || '').trim(),
        badge: this.dataset[`t${n}Badge`] || '',
        suppressed: false,
      }))
      .filter((t) => t.qty > 0);

    const [catalog, rank] = await Promise.all([
      loadCatalog(),
      bestsellerRank(this.dataset.rankFrom || 'shop-all-swim'),
    ]);

    const me = catalog.find((p) => p.handle === this.handle);
    if (!me || me.soldOut) return this.remove();

    const configured = (this.dataset.cats || 'auto').trim();
    this.cats =
      configured && configured !== 'auto'
        ? configured.split(',').map((s) => s.trim())
        : FAMILY[me.cat] || [me.cat];

    const nounSetting = (this.dataset.noun || '').trim();
    this.noun =
      nounSetting && nounSetting !== 'auto' ? nounSetting : NOUN[me.cat] || 'piece';

    this.me = me;

    /*
     * Bags bundle ONE style — the bag being viewed — in different colourways.
     * Offering a rail of every other PVC bag turned a simple "two of these in
     * two colours" decision into a catalogue browse.
     *
     * Dresses and cover-ups are the opposite: mixing prints is the point, so
     * they get the full style rail.
     */
    this.sameByDefault = me.cat === 'bag';

    this.family = this.sameByDefault
      ? []
      : catalog
          .filter(
            (p) =>
              this.cats.includes(p.cat) &&
              !p.thin &&
              p.handle !== me.handle &&
              !NO_BUNDLE.includes(p.handle)
          )
          .sort(sorter(rank));

    const liveVariants = (me.variants || []).filter((v) => v.available).length;
    /* Everything a slot can hold, this product first. */
    this.styles = [me, ...this.family];

    if (!this.family.length && liveVariants < 2) return this.remove();

    this.ship = parseFloat(this.dataset.freeShipping || '75');
    this.tier = this.tiers.find((t) => t.qty === 2) || this.tiers[0];
    this.slots = [];
    this.syncSlots();
    this.render();
  }

  /**
   * Variant chooser for a product: sizes for apparel, colourways for bags.
   *
   * Colourways carry their own variant image, so they are shown as picture
   * swatches. A row of near-identical text labels tells you nothing about a
   * bag; the photograph is the whole decision.
   */
  choices(p) {
    const live = (p.variants || []).filter((v) => v.available);
    if (live.length < 2) return null;

    if (p.sized) {
      return {
        label: 'Size',
        swatches: false,
        values: live.map((v, i) => ({ id: v.id, text: p.sizes[i] || v.title })),
      };
    }

    const opt = (p.options || [])[0];
    const name = typeof opt === 'string' ? opt : (opt && opt.name) || 'Option';
    const values = live.map((v) => ({
      id: v.id,
      text: shortVariantLabel(p, v.title),
      image: (v.featured_image && v.featured_image.src) || '',
    }));
    return { label: name, swatches: values.every((v) => v.image), values };
  }

  firstVariant(p) {
    const live = (p.variants || []).filter((v) => v.available);
    return live.length ? String(live[0].id) : null;
  }

  /**
   * Resize the slot list to the active tier.
   *
   * Nothing is chosen on the customer's behalf: extra slots start empty and it
   * is up to them what goes in. The Add button carries the incomplete state so
   * an empty slot can never read as "the bundle is broken".
   */
  syncSlots() {
    const next = [];
    for (let i = 0; i < this.tier.qty; i++) {
      next[i] =
        this.slots[i] ||
        (this.sameByDefault
          ? { product: this.me, variantId: null }
          : { product: null, variantId: null });
    }
    next[0] = next[0] && next[0].product ? next[0] : { product: this.me, variantId: null };
    this.slots = next;
  }

  /** A slot is ready when it has a product and, where offered, a chosen option. */
  slotReady(slot, index) {
    if (!slot.product) return false;
    const ch = this.choices(slot.product);
    if (!ch) return true;
    if (index === 0 && slot.product.handle === this.me.handle && !slot.variantId) {
      return !!this.mainVariantId();
    }
    return !!slot.variantId;
  }

  /** Slots still waiting on a choice. */
  emptyCount() {
    return this.slots.filter((s, i) => !this.slotReady(s, i)).length;
  }

  mainVariantId() {
    const fromUrl = new URLSearchParams(location.search).get('variant');
    if (fromUrl) return fromUrl;
    const input = document.querySelector(
      'form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]'
    );
    if (input && input.value) return input.value;
    return this.firstVariant(this.me);
  }

  /* ---------------- money ---------------- */

  /**
   * Scroll the theme's own product gallery to the chosen colourway.
   *
   * Picking "Twin Tigers" should change the big picture, otherwise the hero
   * image and the selection disagree. Matched on image filename because the
   * gallery markup carries no variant id.
   */
  showInGallery(slot) {
    if (!slot || !slot.product) return;
    const v = (slot.product.variants || []).find((x) => String(x.id) === String(slot.variantId));
    const src = v && v.featured_image && v.featured_image.src;
    if (!src) return;

    const gallery = document.querySelector(
      '.product-media-gallery, media-gallery, [class*="media-gallery"]'
    );
    if (!gallery) return;

    const key = (u) =>
      String(u || '')
        .split(/[?\s]/)[0]
        .split('/')
        .pop()
        .replace(/\.[a-z0-9]+$/i, '')
        .replace(/_\d+x\d*$/i, '');

    /*
     * The theme leaves every unselected variant's featured image OUT of the
     * gallery, so looking for that exact file always failed silently. The
     * shots that follow it in the product's media are the same colourway
     * though, so walk forward to the first one the gallery actually has.
     */
    const images = slot.product.images || [];
    const from = images.findIndex((u) => key(u) === key(src));
    const wanted = (from >= 0 ? images.slice(from) : [src]).map(key);

    /*
     * Every media is rendered several times over — the slider, the zoom dialog,
     * a thumbnail strip. Only the laid-out copy can be scrolled to; matching a
     * hidden duplicate silently did nothing.
     */
    const pool = [...gallery.querySelectorAll('img')].filter(
      (img) => img.offsetParent !== null && img.offsetWidth > 80
    );
    let match = null;
    for (const want of wanted) {
      match = pool.find((img) =>
        key(img.getAttribute('src') || img.getAttribute('srcset') || img.currentSrc) === want
      );
      if (match) break;
    }
    if (!match) return;

    /*
     * The gallery is the theme's slideshow, and none of the polite ways in
     * work: scrollTo is ignored outright, scrollIntoView fights the snapping,
     * and select() animates so slowly that a second click lands while the
     * first is still queued — which is why every pick showed the colourway
     * before it. Setting scrollLeft with smooth scrolling switched off snaps
     * straight there, and the component picks up its own scroll event so its
     * state stays honest. Re-assert briefly: the snap handler can undo a
     * single assignment.
     */
    const track = match.closest('slideshow-slides');
    if (track && track.clientWidth) {
      let slide = match;
      while (slide.parentElement && slide.parentElement !== track) slide = slide.parentElement;
      const index = [...track.children].indexOf(slide);

      if (index >= 0) {
        const previous = track.style.scrollBehavior;
        const jump = () => {
          track.style.scrollBehavior = 'auto';
          track.scrollLeft = index * track.clientWidth;
        };
        jump();
        requestAnimationFrame(jump);
        setTimeout(() => {
          jump();
          track.style.scrollBehavior = previous;

          /*
           * The slide counter ("1 / 25") is driven by the component's own
           * state, not by scroll position, so a forced jump left the image and
           * the counter disagreeing. Tell it where we landed once we are
           * already there — it has nowhere left to animate to.
           */
          const show = track.closest('slideshow-component');
          if (show && typeof show.select === 'function') show.select(index);
        }, 80);
        return;
      }
    }

    (match.closest('li, .media, [data-media-id]') || match).scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: 'smooth',
    });
  }

  /** Thumbnail for a slot — the chosen colourway where there is one. */
  slotImage(slot) {
    if (!slot.product) return '';
    const v = (slot.product.variants || []).find((x) => String(x.id) === String(slot.variantId));
    return (v && v.featured_image && v.featured_image.src) || slot.product.image;
  }

  /** Price for one slot, honouring the variant chosen inside it. */
  slotMoney(slot, index) {
    if (!slot.product) return { price: 0, was: 0 };
    const id =
      index === 0 && !slot.variantId && slot.product.handle === this.me.handle
        ? this.mainVariantId()
        : slot.variantId;
    return variantMoney(slot.product, id);
  }

  saleTotal() {
    return this.slots.reduce((s, x, i) => s + this.slotMoney(x, i).price, 0);
  }

  wasTotal() {
    return this.slots.reduce((s, x, i) => s + this.slotMoney(x, i).was, 0);
  }

  /**
   * A tier only discounts when a real discount code is wired to it.
   *
   * Showing the percentage before the code exists made the builder quote
   * $131.20 while the cart actually charged $164. Never claim a saving the
   * checkout will not honour.
   */
  bundleSaving(tier) {
    const t = tier || this.tier;
    if (!t.percent || !t.code || t.suppressed) return 0;
    return (this.saleTotal() * t.percent) / 100;
  }

  /**
   * What a tier really costs, given what is in the slots right now.
   *
   * This used to price every tier at (this product × quantity), which is a lie
   * the moment two items differ — a $33 dress beside a $58 one, or two
   * colourways of the same bag. Unfilled slots fall back to this product's
   * price and the total is labelled "from"; the per-unit chip only appears
   * when every unit genuinely costs the same.
   */
  tierNumbers(t) {
    const base = variantMoney(this.me, this.mainVariantId());
    const active = t.qty === this.tier.qty;

    const units = [];
    for (let i = 0; i < t.qty; i++) {
      const slot = active ? this.slots[i] : null;
      units.push(slot && slot.product ? this.slotMoney(slot, i) : null);
    }

    /*
     * A single-item tier is never an estimate: the one unit is this product at
     * the variant the page already has selected.
     */
    const estimate = t.qty > 1 && units.some((u) => !u);
    const filled = units.map((u) => u || base);
    const sale = filled.reduce((n, u) => n + u.price, 0);
    const percent = t.code && !t.suppressed ? t.percent : 0;
    const total = sale - (sale * percent) / 100;

    return {
      estimate,
      sale,
      percent,
      total,
      each: total / t.qty,
      uniform: filled.every((u) => u.price === filled[0].price),
    };
  }

  /* ---------------- rendering ---------------- */

  /**
   * Stacked tier rows: radio, quantity, per-unit price chip, tier total, and a
   * shipping strip underneath. The selected tier expands in place to reveal one
   * chooser row per item, so picking three colourways never leaves the card.
   */
  tierCards() {
    return `
      <div class="ehbb__tiers" role="radiogroup" aria-label="Bundle size">
        ${this.tiers
          .map((t) => {
            const on = t.qty === this.tier.qty;
            const n = this.tierNumbers(t);
            const ships = this.ship > 0 && n.total >= this.ship;

            return `
            <div class="ehbb__tier${on ? ' is-on' : ''}">
              ${t.badge ? `<span class="ehbb__badge">${esc(t.badge)}</span>` : ''}
              <button type="button" class="ehbb__tier-head" data-tier="${t.qty}" role="radio" aria-checked="${on}">
                <span class="ehbb__radio" aria-hidden="true"></span>
                <span class="ehbb__tier-left">
                  <span class="ehbb__tier-qty">${t.qty} ${plural(this.noun, t.qty).toUpperCase()}</span>
                  ${
                    n.percent
                      ? `<span class="ehbb__tier-note">Save ${n.percent}%</span>`
                      : t.qty === 1
                      ? '<span class="ehbb__tier-note">Standard price</span>'
                      : ''
                  }
                </span>
                <span class="ehbb__tier-right">
                  ${
                    n.uniform
                      ? `<span class="ehbb__each">${money(n.each)}<small>/${this.noun}</small></span>`
                      : ''
                  }
                  ${
                    n.uniform && n.percent
                      ? `<s class="ehbb__was-each">${money(n.sale / t.qty)}</s>`
                      : ''
                  }
                </span>
              </button>

              ${on ? `<div class="ehbb__tier-body">${this.slotRows()}</div>` : ''}

              <p class="ehbb__ship-strip${ships ? ' is-free' : ''}">
                ${
                  ships
                    ? 'Free shipping'
                    : this.ship
                    ? `Free shipping on orders ${money(this.ship)}+`
                    : ''
                }
              </p>
            </div>`;
          })
          .join('')}
      </div>`;
  }

  /**
   * One identical control per slot: scroll the styles on top, choose the size
   * (or colourway) underneath. Slot 1 is pre-selected to the product being
   * viewed but is otherwise the same control as every other slot — special-
   * casing it made the panel read as two unrelated widgets stacked together.
   */
  slotRows() {
    return `
      <ol class="ehbb__slots">
        ${this.slots
          .map((slot, i) => {
            const p = slot.product;
            const ch = p ? this.choices(p) : null;
            const chosen = ch && ch.values.find((v) => String(v.id) === String(slot.variantId));
            const ordinal = ['1st', '2nd', '3rd', '4th', '5th', '6th'][i] || `#${i + 1}`;

            return `
            <li class="ehbb__slot" data-slot="${i}">
              <p class="ehbb__slot-title">
                <span class="ehbb__slot-ord">${ordinal}</span>
                <span class="ehbb__slot-name">${
                  this.styles.length > 1
                    ? p
                      ? p.title
                      : `Choose your ${ordinal} ${this.noun}`
                    : chosen
                    ? chosen.text
                    : `Choose a ${(ch && ch.label ? ch.label : 'colour').toLowerCase()}`
                }</span>
              </p>

              ${
                this.styles.length > 1
                  ? `<div class="ehbb__picker-rail">
                ${this.styles
                  .map(
                    (f) => `
                  <div class="ehbb__opt-wrap">
                    <button type="button" class="ehbb__opt${
                      p && p.handle === f.handle ? ' is-on' : ''
                    }" data-choose="${i}" data-handle="${f.handle}" title="${esc(f.title)}">
                      <img src="${thumb(f.image, 220)}" alt="${esc(f.title)}" loading="lazy" width="72" height="90">
                      ${(() => {
                        const m = variantMoney(f, null);
                        return `<span class="ehbb__opt-price">${money(m.price)}${
                          m.was > m.price ? `<s>${money(m.was)}</s>` : ''
                        }</span>`;
                      })()}
                    </button>
                    <button type="button" class="ehbb__zoom" data-zoom="${f.handle}" aria-label="See more images of ${esc(
                      f.title
                    )}">${ZOOM_ICON}</button>
                  </div>`
                  )
                  .join('')}
              </div>`
                  : ''
              }

              ${
                ch
                  ? `${
                      /* With no style rail the slot heading already names the
                         choice, so a second "Color: Wild Tiger" line under it
                         is the same words again, one row further down. */
                      this.styles.length > 1
                        ? `<p class="ehbb__opt-label">${ch.label}${
                            chosen ? `: <strong>${chosen.text}</strong>` : ''
                          }</p>`
                        : ''
                    }
                     <div class="${ch.swatches ? 'ehbb__swatches' : 'ehbb__sizes'}" role="group" aria-label="${ch.label} for item ${i + 1}">
                       ${ch.values
                         .map((v) =>
                           ch.swatches
                             ? `<button type="button" class="ehbb__swatch${
                                 String(slot.variantId) === String(v.id) ? ' is-on' : ''
                               }" data-size="${i}" data-vid="${v.id}" title="${esc(v.text)}" aria-label="${esc(v.text)}">
                                  <img src="${thumb(v.image, 160)}" alt="" loading="lazy" width="56" height="56">
                                </button>`
                             : `<button type="button" class="ehbb__size${
                                 String(slot.variantId) === String(v.id) ? ' is-on' : ''
                               }" data-size="${i}" data-vid="${v.id}">${v.text}</button>`
                         )
                         .join('')}
                     </div>`
                  : ''
              }
            </li>`;
          })
          .join('')}
      </ol>`;
  }

  render() {
    /*
     * Re-rendering resets every rail to the far left, so picking the 9th dress
     * threw the shopper back to the 1st. Remember where each rail was scrolled
     * to and put it back after the repaint.
     */
    const scrolled = {};
    this.querySelectorAll('.ehbb__picker-rail').forEach((rail) => {
      const slot = rail.closest('[data-slot]');
      if (slot) scrolled[slot.dataset.slot] = rail.scrollLeft;
    });

    const missing = this.emptyCount();
    const sale = this.saleTotal();
    const was = this.wasTotal();
    const saving = this.bundleSaving();
    const total = sale - saving;
    const totalOff = was > total ? was - total : 0;
    const pct = was > 0 ? Math.round((totalOff / was) * 100) : 0;

    this.innerHTML = `
      <div class="ehbb">
        <div class="ehbb__head">
          <p class="ehbb__title">${this.dataset.heading || 'BUILD YOUR BUNDLE'}</p>
        </div>

        ${this.tierCards()}

        <div class="ehbb__foot">
          <button type="button" class="ehbb__add${missing ? ' is-waiting' : ''}" data-add>
            ${
              missing
                ? `Pick ${missing} more to continue`
                : `${
                    this.tier.qty > 1
                      ? `Add ${this.tier.qty} ${plural(this.noun, this.tier.qty)}`
                      : 'Add to bag'
                  } · ${money(total)}${
                    /* What it would have cost at full price. Sits after the
                       total in a smaller face so the row never wraps. */
                    was > total ? `<s>${money(was)}</s>` : ''
                  }`
            }
          </button>
          <p class="ehbb__msg" data-msg role="status" aria-live="polite"></p>
        </div>
      </div>`;

    this.querySelectorAll('[data-tier]').forEach((b) =>
      b.addEventListener('click', () => {
        this.tier = this.tiers.find((t) => t.qty === Number(b.dataset.tier));
        this.syncSlots();
        this.render();
      })
    );

    this.querySelectorAll('[data-choose]').forEach((b) =>
      b.addEventListener('click', () => {
        const i = Number(b.dataset.choose);
        const chosen = this.styles.find((f) => f.handle === b.dataset.handle);
        this.slots[i] = {
          product: chosen,
          /* a bag has real colourways to choose; anything single-variant can
             just take its only variant */
          variantId: this.choices(chosen) ? null : this.firstVariant(chosen),
        };
        this.showInGallery(this.slots[i]);
        this.render();
      })
    );

    this.querySelectorAll('[data-size]').forEach((b) =>
      b.addEventListener('click', () => {
        const i = Number(b.dataset.size);
        this.slots[i].variantId = b.dataset.vid;
        /*
         * Any slot drives the gallery. Picking "Twin Tigers" in slot 3 should
         * still show Twin Tigers up top — that is the whole reason to click a
         * colour you cannot see properly at swatch size.
         */
        this.showInGallery(this.slots[i]);
        this.render();
      })
    );

    this.querySelectorAll('[data-zoom]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        const p = this.styles.find((f) => f.handle === b.dataset.zoom);
        if (p) openImages(p);
      })
    );

    this.querySelector('[data-add]').addEventListener('click', () => this.add());

    Object.keys(scrolled).forEach((i) => {
      const rail = this.querySelector(`[data-slot="${i}"] .ehbb__picker-rail`);
      if (rail) rail.scrollLeft = scrolled[i];
    });

    this.watchMainVariant();
    this.syncFormVariant();

    const unlocked = total >= this.ship && this.ship > 0;
    if (unlocked && this._wasLocked) {
      const note = this.querySelector('.ehbb__ship');
      if (note) {
        note.classList.add('is-pop');
        setTimeout(() => note.classList.remove('is-pop'), 700);
      }
    }
    this._wasLocked = !unlocked;
  }

  /**
   * Mirror slot 1's choice into the theme's hidden variant input.
   *
   * The in-box Add to cart is hidden on these products but the accelerated
   * checkout button remains, and it buys whatever that input holds. Without
   * this it would silently buy the default colour or size.
   */
  syncFormVariant() {
    const id = this.slots[0] && this.slots[0].variantId;
    if (!id) return;
    document
      .querySelectorAll('form[action*="/cart/add"] input[name="id"]')
      .forEach((input) => {
        if (input.value !== String(id)) {
          input.value = String(id);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    this._lastMain = String(id);
  }

  /** Repaint when the buy box's variant changes so the totals stay truthful. */
  watchMainVariant() {
    if (this._watching) return;
    this._watching = true;
    this._lastMain = this.mainVariantId();
    const check = () => {
      const now = this.mainVariantId();
      if (now !== this._lastMain) {
        this._lastMain = now;
        this.render();
      }
    };
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form) form.addEventListener('change', () => setTimeout(check, 60));
    document.addEventListener('variant:update', check);
    this._poll = setInterval(check, 600);
  }

  disconnectedCallback() {
    if (this._poll) clearInterval(this._poll);
  }

  msg(text, isError) {
    const el = this.querySelector('[data-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.classList.toggle('is-error', !!isError);
  }

  /** Make a problem impossible to miss rather than a grey line at the bottom. */
  flag(i, text) {
    this.msg(text, true);
    const row = this.querySelector(`[data-sizes="${i}"]`) || this.querySelector(`[data-slot="${i}"]`);
    if (row) {
      row.classList.add('is-flagged');
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setTimeout(() => row.classList.remove('is-flagged'), 2200);
    }
  }

  /**
   * One button only. Shop Pay's express button is hidden on bundle pages
   * because it submits the product form — one variant, one quantity — and
   * would silently check out a single item instead of the bundle.
   */
  async add() {
    const btn = this.querySelector('[data-add]');
    const items = [];

    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      const p = slot.product;
      if (!p) return this.flag(i, `Choose a ${this.noun} for item ${i + 1}.`);

      let id = slot.variantId;
      if (i === 0 && p.handle === this.me.handle && !id) id = this.mainVariantId();
      if (!id && !this.choices(p)) id = this.firstVariant(p);
      if (!id) {
        return this.flag(i, `Choose a colour for item ${i + 1}.`);
      }
      items.push({ id: Number(id), quantity: 1 });
    }

    btn.disabled = true;
    const original = btn.innerHTML;
    btn.textContent = 'Adding…';

    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart.');
      }

      let landed = false;
      if (this.tier.code) {
        /*
         * A code from an earlier bundle survives /cart/clear.js and refuses to
         * be replaced by a weaker one, so a 3-item code kept paying out on a
         * 2-item order. Clear whatever is attached before applying this tier's
         * code, and confirm the code that landed is the one we quoted rather
         * than just "some discount happened".
         */
        await fetch(`${root()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: '' }),
        }).catch(() => {});

        await fetch(`${root()}discount/${encodeURIComponent(this.tier.code)}`, {
          credentials: 'same-origin',
        }).catch(() => {});

        const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
        const code = this.tier.code.toUpperCase();
        landed = (cart.items || []).some((i) =>
          (i.discounts || []).some((d) => String(d.title).toUpperCase() === code)
        );
        if (!landed) {
          this.tier.suppressed = true;
          this.render();
        }
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));

      btn.innerHTML = 'Added';
      openCartDrawer();

      setTimeout(() => {
        btn.innerHTML = original;
        btn.disabled = false;
      }, 1800);
    } catch (e) {
      this.msg(e.message || 'Something went wrong.', true);
      btn.innerHTML = original;
      btn.disabled = false;
    }
  }
}

if (!customElements.get('eh-bundle-builder')) {
  customElements.define('eh-bundle-builder', EhBundleBuilder);
}
